import { Button } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import { observer } from 'mobx-react'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import { usePortal } from 'modules/core/lib/portalContext'

import type { RecommendationAction } from 'modules/timeoff/components/LeaveApprovalRecommendation/RecommendationAction'
import type LeaveForm from 'modules/timeoff/lib/LeaveForm'
import { useLeaves } from 'modules/timeoff/lib/LeavesContextProvider'
import { trackApprovalRecommendation } from 'modules/timeoff/lib/tracking'
import type Leave from 'modules/timeoff/store/models/Leave'

interface RejectButtonProps {
  form: LeaveForm
  leave: Leave
  onReject: () => void
  onFetchStats: () => void
  onChange: () => void
  canApprove: boolean
  canUpdate: boolean
  canRemove: boolean
  isManagerOrAdmin: boolean | null | undefined
  isLoading: boolean
  isCurrentApprover?: boolean
  approvalFlowId?: string
  fromShifts?: boolean
  employeeId: string
  recommendationAction?: RecommendationAction
  canApproveFromFlow?: boolean
}

const RejectButton = ({
  form,
  leave,
  onReject,
  onFetchStats,
  onChange,
  canApprove,
  canUpdate,
  canRemove,
  isManagerOrAdmin,
  isLoading,
  isCurrentApprover,
  fromShifts = false,
  employeeId,
  recommendationAction,
  canApproveFromFlow,
}: RejectButtonProps) => {
  const { closePortal } = usePortal()
  const { fetchData } = useLeaves()

  const onClickReject = async () => {
    track(trackingEvents.timeoff.reviewTimeOffRequestSubmit, {
      request_action: 'reject_intermediate',
      from_shifts: fromShifts,
    })

    if (recommendationAction) {
      trackApprovalRecommendation('reject', recommendationAction)
    }

    if (canRemove && canUpdate) {
      await asyncAction(() => form.save(leave))
    }

    closePortal({
      preventOnCloseCallbackAfterClose: true,
    })

    return onReject()
  }

  const { performAction: onDestroy } = useAsyncAction(async () => {
    await leave.delete()
    onFetchStats()
    // Refreshes the context's leaves collection so that the deleted leave
    // is removed from the displayed list of leaves
    fetchData(employeeId)
    closePortal()

    if (onChange) onChange()
  })

  // Use canApproveFromFlow from backend if available, as it includes proper permission checks
  // (including the 'owned' context that prevents self-approval when ApproveLeaves has owned: false)
  // Falls back to frontend logic for backward compatibility when canApproveFromFlow is undefined.
  // While loading, treat the approval permission as false to prevent showing reject/approve
  // buttons based on stale or incomplete data from the fallback path.
  const canApproveOrReject =
    canApproveFromFlow !== undefined
      ? canApproveFromFlow
      : isLoading
        ? false
        : canApprove || isCurrentApprover

  const canReject = canApproveOrReject && leave.isPending()
  // Use canReject (not canApproveOrReject) to determine action:
  // - If canReject is true (pending leave + can approve): show reject modal
  // - Otherwise (approved/rejected leave OR no approve permission): delete directly
  const onClick = canReject ? onClickReject : onDestroy

  if (!isManagerOrAdmin && leave.isRejected()) return null
  if (!canReject && !canRemove) return null

  const today = moment()
  const finish = moment(leave.attributes.finish_on).endOf('day')
  const pastLeave = today.isAfter(finish)

  const updateButton =
    isManagerOrAdmin && !leave.isRejected() && (leave.isPending() || !pastLeave)
      ? i18n.t('common.reject')
      : i18n.t('common.destroy')

  const button = canReject
    ? i18n.t('common.reject')
    : canRemove
      ? i18n.t('common.destroy')
      : updateButton

  return (
    <Button type="negative" onClick={onClick} disabled={isLoading}>
      {button}
    </Button>
  )
}

export default observer(RejectButton)
